import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../utils/ApiUrl";

/**
 * @param {*} payload — axios response body
 * @returns {number} remainingCredit (USD), clamped to >= 0
 */
export function parseCreditBalanceResponse(payload) {
  const n = Number(payload?.data?.remainingCredit);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
}

/**
 * Credits usable for $KAI from a special-round purchase (1 credit ≈ $1 permission).
 * e.g. 500 total credits + $1,000 purchase → 500 KAI credits;
 *      1,000 total credits + $500 purchase → 500 KAI credits.
 */
export function getKaiCreditsFromSpecialPurchase(
  totalCreditsUsd,
  specialPurchaseUsd,
) {
  const total = Math.max(0, Number(totalCreditsUsd) || 0);
  const purchase = Math.max(0, Number(specialPurchaseUsd) || 0);
  return Math.min(total, purchase);
}

/**
 * Fetches user credit balance for the special presale round.
 * @param {string|undefined|null} account
 * @param {boolean} enabled — only fetch when special round is active
 */
export default function useCreditBalance(account, enabled) {
  const [remainingCreditUsd, setRemainingCreditUsd] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refetchCreditBalance = useCallback(async () => {
    if (!enabled || !account) {
      setRemainingCreditUsd(0);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}credit/credit/balance`, {
        params: { by: account.toString() },
      });
      setRemainingCreditUsd(parseCreditBalanceResponse(res.data));
    } catch (e) {
      console.warn("useCreditBalance:", e?.message || e);
      setError(e);
      setRemainingCreditUsd(0);
    } finally {
      setLoading(false);
    }
  }, [account, enabled]);

  useEffect(() => {
    refetchCreditBalance();
    if (!enabled || !account) return undefined;
    const t = setInterval(refetchCreditBalance, 25000);
    return () => clearInterval(t);
  }, [refetchCreditBalance, enabled, account]);

  return {
    remainingCreditUsd,
    loading,
    error,
    refetchCreditBalance,
  };
}
